import { MessageProcessor } from '@/services/websocketProcessorTypes'
import store from '@/redux/store'
import {
  loadMoreAdvisorMessages,
  setAdvisorChatLoadingMore,
  setIsGenerating,
  setLoadingAdvisorMsgs,
  setupAdvisorMessages,
  stopAllStreamingMessages,
  updateAdvisorMessage,
  resourceLimitsUpdate,
  setResourceLimits,
  incrementAdvisorUnread,
} from '@/redux/slices/chatSlice'
import { ActiveTab } from '@/constants/chat'
import { updatePlayerAdvisorDistributionDelta } from '@/redux/slices/usageStatsSlice'
import { SenderType } from '@/constants/chat'
import { commonProcessors } from '@/services/websocketProcessors/commonProcessors'
import { toast } from 'sonner'

export const advisorProcessors: MessageProcessor[] = [
  {
    type: 'chat_history',
    handler: (data) => {
      store.dispatch(
        setupAdvisorMessages({
          messages: data.messages,
          hasMore: data.has_more,
          totalCount: data.total_count,
          currentPage: data.current_page,
        })
      )
      store.dispatch(setLoadingAdvisorMsgs(false))
    },
  },

  {
    type: 'load_more_messages_response',
    handler: (data) => {
      store.dispatch(
        loadMoreAdvisorMessages({
          messages: data.messages,
          hasMore: data.has_more,
          totalCount: data.total_count,
          currentPage: data.current_page,
        })
      )
      store.dispatch(setAdvisorChatLoadingMore(false))
    },
  },
  {
    type: 'chat_message',
    handler: (data) => {
      const messageId = data.message_id || `${data.user}-${Date.now()}`
      const state = store.getState()
      const isNewMessage = !state.chat.advisorChatMessages.some(
        (msg) => msg.id === messageId
      )

      if (
        isNewMessage &&
        data.sender_type === SenderType.Chatbot &&
        state.chat.activeTab !== ActiveTab.Advisor
      ) {
        store.dispatch(incrementAdvisorUnread())
      }

      const isBotFinalizing =
        data.sender_type === SenderType.Chatbot &&
        (data.is_final || data.is_streaming === false)

      store.dispatch(
        updateAdvisorMessage({
          id: messageId,
          message: data.message,
          isSender: false,
          streaming: data.is_streaming,
          user: data.user,
          sender_type: data.sender_type,
          phaseId: data.phaseId,
          created_at: isBotFinalizing
            ? data.created_at ?? new Date().toISOString()
            : data.created_at,
          multiAdvisorGroupId: data.multi_advisor_group_id,
          llm_provider: data.llm_provider,
          llm_model_name: data.llm_model_name,
        })
      )

      if (data.sender_type === SenderType.Chatbot && data.is_streaming) {
        store.dispatch(setIsGenerating(true))
      }

      if (
        data.is_final ||
        (!data.is_streaming && data.sender_type === SenderType.Chatbot)
      ) {
        store.dispatch(setIsGenerating(false))
      }
    },
  },
  {
    type: 'generation_stopped',
    handler: () => {
      store.dispatch(stopAllStreamingMessages())
      store.dispatch(setIsGenerating(false))
    },
  },
  {
    type: 'resource_status_update',
    handler: (data) => {
      store.dispatch(
        resourceLimitsUpdate({
          usage: data.usage,
          remaining: data.remaining,
          limits: data.limits,
        })
      )
    },
  },
  {
    type: 'resource_limits_initial',
    handler: (data) => {
      store.dispatch(
        setResourceLimits({
          resource_limits_enabled: data.resource_limits_enabled,
          resource_enforcement_mode: data.resource_enforcement_mode,
          message_limit: data.message_limit,
          token_limit: data.token_limit,
          currency_limit: data.currency_limit,
          usage: data.usage,
          remaining: data.remaining,
        })
      )
    },
  },
  {
    type: 'resource_limit_exceeded',
    handler: (data) => {
      toast.error(data.message || 'Message blocked due to resource limits', {
        duration: 3000,
      })

      store.dispatch(setIsGenerating(false))

      if (data.remaining) {
        store.dispatch(
          resourceLimitsUpdate({
            remaining: data.remaining,
          })
        )
      }
    },
  },
  {
    type: 'resource_limit_warning',
    handler: (data) => {
      toast.warning(data.message || 'You are exceeding your resource limits', {
        duration: 3000,
      })

      if (data.remaining) {
        store.dispatch(
          resourceLimitsUpdate({
            remaining: data.remaining,
          })
        )
      }
    },
  },
  {
    type: 'advisor_distribution_update',
    handler: (data) => {
      store.dispatch(
        updatePlayerAdvisorDistributionDelta({
          player_id: data.player_id,
          chatbot_id: data.chatbot_id,
          chatbot_name: data.chatbot_name,
          delta: data.delta ?? 1,
        })
      )
    },
  },
  ...commonProcessors,
]
